import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Edit,
    Calendar,
    CreditCard,
    DollarSign,
    CheckCircle,
    XCircle,
    Users,
    Clock
} from 'lucide-react';
import { useGetMembershipPlanQuery, MembershipPlan } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';

const ViewMembershipPlan: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const planId = id ? parseInt(id) : null;
    const isValidId = planId && !isNaN(planId) && planId > 0;

    const { data: planResponse, isLoading, error, refetch } = useGetMembershipPlanQuery(planId!, {
        skip: !isValidId,
        refetchOnMountOrArgChange: true
    });

    const plan: MembershipPlan | undefined = planResponse?.data;

    const formatCurrency = (amount: string | number) => {
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(num);
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    const formatDateTime = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const getStatusColor = (isActive: boolean) => {
        return isActive
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800';
    };

    const getAccessLevelColor = (level: string | null) => {
        switch (level) {
            case 'platinum': return 'bg-purple-100 text-purple-800';
            case 'gold': return 'bg-yellow-100 text-yellow-800';
            case 'silver': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading membership plan details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Plan</h2>
                    <p className="text-gray-600 mb-6">
                        {(error as any)?.data?.message || 'There was an error loading the membership plan details. Please try again.'}
                    </p>
                    <div className="space-x-4">
                        <button
                            onClick={() => refetch()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                        <button
                            onClick={() => navigate('/admin/membership-management')}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            ← Back to Membership Plans
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Plan Not Found</h2>
                    <p className="text-gray-600 mb-6">The membership plan you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/membership-management')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Membership Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/membership-management')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">{plan.name}</h1>
                                <p className="text-gray-600">Membership Plan Details</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate(`/admin/membership-management/edit/${plan.plan_id}`)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                            >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Plan
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Status Card */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <CreditCard className="w-8 h-8 text-blue-600" />
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                                    <p className="text-sm text-gray-600">Plan ID: {plan.plan_id}</p>
                                </div>
                            </div>
                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(plan.is_active)}`}>
                                {plan.is_active ? (
                                    <>
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        Active
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="w-4 h-4 mr-1" />
                                        Inactive
                                    </>
                                )}
                            </span>
                        </div>
                    </div>

                    {/* Plan Details */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <CreditCard className="w-5 h-5 mr-2" />
                                Plan Information
                            </h2>
                        </div>
                        <div className="p-6 space-y-6">
                            {plan.description && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <p className="text-gray-900">{plan.description}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <DollarSign className="w-4 h-4 mr-2" />
                                        Price
                                    </label>
                                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(plan.price)}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <Calendar className="w-4 h-4 mr-2" />
                                        Duration
                                    </label>
                                    <p className="text-lg font-semibold text-gray-900">{plan.duration_days} days</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Payment Type
                                    </label>
                                    <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                                        {plan.payment_type === 'monthly_installment' ? 'Monthly Installment' : 'One Time Payment'}
                                    </span>
                                </div>

                                {plan.access_level && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Access Level
                                        </label>
                                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full capitalize ${getAccessLevelColor(plan.access_level)}`}>
                                            {plan.access_level}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {plan.payment_type === 'monthly_installment' && plan.monthly_installment_amount && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-white border border-gray-200 rounded-lg">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Monthly Installment Amount (USD)
                                        </label>
                                        <p className="text-xl font-bold text-blue-600">{formatCurrency(plan.monthly_installment_amount)}</p>
                                    </div>
                                    {plan.monthly_installment_count && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Number of Installments
                                            </label>
                                            <p className="text-xl font-bold text-blue-600">{plan.monthly_installment_count} months</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                    <Users className="w-4 h-4 mr-2" />
                                    Max Bookings Per Month
                                </label>
                                <p className="text-lg font-semibold text-gray-900">
                                    {plan.max_bookings_per_month === 0 ? 'Unlimited' : plan.max_bookings_per_month}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Timestamps */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Clock className="w-5 h-5 mr-2" />
                                Timestamps
                            </h2>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Created At
                                    </label>
                                    <p className="text-gray-900">{formatDateTime(plan.created_at)}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Updated At
                                    </label>
                                    <p className="text-gray-900">{formatDateTime(plan.updated_at)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Active Memberships */}
                    {plan.memberships && plan.memberships.length > 0 && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Users className="w-5 h-5 mr-2" />
                                    Active Memberships ({plan.memberships.length})
                                </h2>
                            </div>
                            <div className="p-6">
                                <p className="text-sm text-gray-600">
                                    This plan has {plan.memberships.length} active membership(s). 
                                    You cannot delete plans with active memberships.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewMembershipPlan;

